import { Badge, Button, Input, Modal, Textarea } from '@any-ui-react/core';
import { addDays, addMonths, format, isAfter, parseISO } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Calendar } from '@/common/components/shared/calendar';
import { ModalFooter } from '@/common/components/shared/modal-footer';
import { OrdinalNumber } from '@/common/components/shared/ordinal-number';
import { toast } from '@/common/components/shared/toaster';
import {
  CompanyProbationReviewTermMode,
  useExtendProbationMutation,
  useGetEmployeeProbationReviewTermQuery,
} from '@/common/graphql';
import { ProbationReviewExtendFormData, type ProbationReviewType } from '@/common/types';
import { ApiUtils, DATETIME_FORMATS, DateUtils, evictProbationReviewAuditLogs } from '@/common/utils';

interface Props {
  opened: boolean;
  /** The employee whose probation is extended. */
  target: ProbationReviewType | null;
  onClose: () => void;
  /** Called once the extension is saved, so the list can reload. */
  onCompleted: () => void;
}

const API_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * The current end date (the latest Due Date), the earliest allowed new one (the day after) and the proposed one: the
 * current end date plus the company's review interval (for example 1 month), once that interval has arrived.
 */
const useExtensionDates = (opened: boolean, target: ProbationReviewType | null) => {
  const currentEndDate = useMemo(() => target?.probationEndDate ?? target?.reviews.at(-1)?.deadline ?? null, [target]);
  const minDate = useMemo(() => (currentEndDate ? addDays(parseISO(currentEndDate), 1) : new Date()), [currentEndDate]);

  const { data: termData } = useGetEmployeeProbationReviewTermQuery({
    variables: { id: target?.employeeId ?? '' },
    skip: !opened || !target,
  });
  const term = termData?.employee?.currentMainContract?.company.probationReviewTerm;
  const defaultDate = useMemo(() => {
    if (!(currentEndDate && term)) {
      return minDate;
    }
    const end = parseISO(currentEndDate);
    const proposed =
      term.mode === CompanyProbationReviewTermMode.Month ? addMonths(end, term.length) : addDays(end, term.length);

    return isAfter(proposed, minDate) ? proposed : minDate;
  }, [currentEndDate, term, minDate]);

  return { currentEndDate, minDate, defaultDate };
};

interface ExtensionSummaryProps {
  currentEndDate: string | null;
  reviews: ProbationReviewType['reviews'];
  newEndDate: Date;
}

/** The current end date, every review with its Due Date, and the review the extension adds. */
const ExtensionSummary = ({ currentEndDate, reviews, newEndDate }: ExtensionSummaryProps) => (
  <div className="flex-1 border px-4 py-6 text-sm">
    <div className="flex w-full justify-between pb-4 font-semibold">
      <span>Current Probation End Date</span>
      <span data-testid="current-end-date">
        {currentEndDate ? DateUtils.formatDate(currentEndDate, DATETIME_FORMATS.MMMddyyyy) : '-'}
      </span>
    </div>
    <div className="flex flex-col divide-y border-y text-gray-7">
      {reviews.map((review, index) => (
        <div key={`${review.deadline}-${index}`} className="flex w-full justify-between py-2" data-testid="review-row">
          <span>
            <OrdinalNumber value={index + 1} />
            &nbsp;Review
          </span>
          <span>{DateUtils.formatDate(review.deadline, DATETIME_FORMATS.MMMddyyyy)}</span>
        </div>
      ))}
    </div>
    <div className="my-4 flex justify-between font-semibold">
      <span>
        <OrdinalNumber value={reviews.length + 1} />
        &nbsp;Review
      </span>
      <div className="flex items-center space-x-1">
        <Badge color="yellow" size="sm">
          New
        </Badge>
        <span data-testid="new-end-date">{DateUtils.formatDate(newEndDate, DATETIME_FORMATS.MMMddyyyy)}</span>
      </div>
    </div>
  </div>
);

export const ExtendProbationDialog = ({ opened, target, onClose, onCompleted }: Props) => {
  // The new end date must be later than the current one, and the added review is due on it.
  const { currentEndDate, minDate, defaultDate } = useExtensionDates(opened, target);

  const [newEndDate, setNewEndDate] = useState<Date>(defaultDate);
  // The calendar keeps its own copy of the value, so it is rebuilt together with the reset (not before it).
  const [calendarKey, setCalendarKey] = useState(0);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [extendProbation, { loading }] = useExtendProbationMutation({
    onError(apiError) {
      ApiUtils.handleApiError(apiError);
    },
  });

  // The default date can change after the dialog opened (the company term arrives later). Opening resets the form; a
  // later change of the default replaces only a date the user has not picked, and never the reason.
  const wasOpened = useRef(false);
  const dateEdited = useRef(false);
  useEffect(() => {
    if (!opened) {
      wasOpened.current = false;
      return;
    }

    if (!wasOpened.current) {
      wasOpened.current = true;
      dateEdited.current = false;
      setReason('');
      setError(null);
    } else if (dateEdited.current) {
      return;
    }

    setNewEndDate(defaultDate);
    setCalendarKey(key => key + 1);
  }, [opened, defaultDate]);

  const reviews = target?.reviews ?? [];

  const handleSubmit = async () => {
    const parsed = ProbationReviewExtendFormData.safeParse({
      newEndDate: format(newEndDate, API_DATE_FORMAT),
      reason,
    });
    if (!parsed.success || !target) {
      setError(parsed.success ? null : (parsed.error.issues[0]?.message ?? null));
      return;
    }
    setError(null);

    const { data } = await extendProbation({
      variables: {
        input: { employeeId: target.employeeId, newEndDate: parsed.data.newEndDate, reason: parsed.data.reason.trim() },
      },
      update: cache => evictProbationReviewAuditLogs(cache, [target.employeeId]),
    });
    if (!data?.extendProbation.id) {
      return;
    }

    toast.success('Probation extended');
    onCompleted();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      title={`Extend Probation${target ? `: ${target.employeeName}` : ''}`}
      size="xl"
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        <div className="flex w-full space-x-6">
          <div className="border px-6 py-4">
            <Calendar
              key={calendarKey}
              minDate={minDate}
              value={newEndDate}
              onChange={date => {
                dateEdited.current = true;
                setError(null);
                setNewEndDate(date);
              }}
            />
          </div>
          <ExtensionSummary currentEndDate={currentEndDate} newEndDate={newEndDate} reviews={reviews} />
        </div>

        <Textarea
          autosize={true}
          label="Reason"
          minRows={3}
          value={reason}
          withAsterisk={true}
          onChange={event => {
            setReason(event.currentTarget.value);
            setError(null);
          }}
        />
        <Input.Error>{error}</Input.Error>

        <ModalFooter>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={loading} onClick={handleSubmit}>
            Extend Probation
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};
